import { PDFRenderer, Show } from "components";
import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import useApi from "utils/use-api";
import { fetchSignedPlacingSlipBlobUrl } from "./placing-slip-api";

const clearBlobUrl = (setter: Dispatch<SetStateAction<string | null>>) => {
    setter((prev) => {
        if (prev) {
            URL.revokeObjectURL(prev);
        }
        return null;
    });
};

type Props = {
    offerId: string;
    reinsurerId: string;
    enabled?: boolean;
};

const SignedPlacingSlipViewer = ({
    offerId,
    reinsurerId,
    enabled = true,
}: Props) => {
    const api = useApi();
    const [slipUrl, setSlipUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [hasSlip, setHasSlip] = useState(false);

    useEffect(() => {
        let active = true;

        if (!enabled || !offerId || !reinsurerId) {
            clearBlobUrl(setSlipUrl);
            setHasSlip(false);
            setLoading(false);
            return;
        }

        const loadSignedSlip = async () => {
            setLoading(true);

            try {
                const blobUrl = await fetchSignedPlacingSlipBlobUrl(
                    api,
                    offerId,
                    reinsurerId,
                );
                if (!active) {
                    URL.revokeObjectURL(blobUrl);
                    return;
                }
                setSlipUrl((prev) => {
                    if (prev) {
                        URL.revokeObjectURL(prev);
                    }
                    return blobUrl;
                });
                setHasSlip(true);
            } catch {
                if (active) {
                    setHasSlip(false);
                    clearBlobUrl(setSlipUrl);
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        loadSignedSlip();

        return () => {
            active = false;
        };
    }, [offerId, reinsurerId, enabled]);

    return (
        <div className="bg-white rounded-2xl shadow">
            <Show if={loading}>
                <div className="flex-1 w-full min-h-[800px] flex flex-col items-center justify-center space-y-4 bg-card-500">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500" />
                    <p className="text-gray-500 font-medium">
                        Loading signed placing slip...
                    </p>
                </div>
            </Show>
            <Show if={!loading && !hasSlip}>
                <div className="flex-1 w-full min-h-[400px] flex flex-col items-center justify-center space-y-4 bg-card-500 p-6">
                    <p className="text-gray-500 text-center font-medium">
                        No signed placing slip available for this participant.
                    </p>
                </div>
            </Show>
            <Show if={!loading && hasSlip && Boolean(slipUrl)}>
                <PDFRenderer url={slipUrl || ""} />
            </Show>
        </div>
    );
};

export default SignedPlacingSlipViewer;
